import { action, makeAutoObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { logWithState } from '../functions/console.function';
import { useStoreCommon } from '../hooks/useStore.hook';
import { Languages } from '../types/lang.type';
import { RootStoreMobX } from './root.store';

const languageCodeMapper = {
  fr_FR: 'fr',
  en_EN: 'en',
  de_DE: 'de',
  it_IT: 'it',
} as const satisfies Record<Languages, string>;

export type TranslationItem = {
  lang: string;
  [key: string]: string;
};

export type TranslateOptions = {
  availableLanguages?: string[];

  notification?: {
    success?: string;
    error?: string;
  };
  onSuccess?: () => void;
  onError?: (error: Error) => void;
};

export class AIStoreMobX {
  rootStore: RootStoreMobX;
  @observable public isTranslating: boolean = false;
  @observable public googleTranslateApiKey: string | null = null;
  @observable public load: boolean = false;

  constructor(rootStore: RootStoreMobX) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  @action public init({ apiKey }: { apiKey: { translator?: string | null } }) {
    runInAction(() => {
      this.load = false;
      this.googleTranslateApiKey = apiKey.translator || null;
      this.load = true;
    });
  }

  @action public setIsTranslating(value: boolean) {
    this.isTranslating = value;
  }

  private async translateText(
    text: string,
    targetLang: string,
    sourceLang: string | null,
    apiKey: string,
  ): Promise<string> {
    const url = `https://translation.googleapis.com/language/translate/v2?key=${apiKey}`;

    try {
      const body: any = {
        q: text,
        target: targetLang,
        format: 'text',
      };

      // Si sourceLang est fourni, on l'utilise, sinon Google Translate détecte automatiquement
      if (sourceLang) {
        body.source = sourceLang;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Translation failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.data.translations[0].translatedText;
    } catch (error) {
      console.error('Translation error:', error);
      throw error;
    }
  }

  /**
   * Traduit automatiquement les champs manquants dans un tableau de traductions
   * @param translations - Tableau des traductions avec lang obligatoire et n'importe quels champs additionnels
   * @param options - Options de traduction
   * @returns Tableau des traductions mises à jour
   */
  @action public async translate(
    translations: TranslationItem[],
    options?: TranslateOptions,
  ): Promise<TranslationItem[]> {
    const { notification, onSuccess, onError } = options || {};

    const apiKey = this.googleTranslateApiKey;

    if (!apiKey) {
      const error = new Error('Google Translate API key is missing');
      console.error(error.message);

      if (notification?.error) {
        this.rootStore.NotificationStore.emit({
          mode: 'ERROR',
          content: notification.error,
        });
      }

      onError?.(error);
      throw error;
    }

    this.setIsTranslating(true);

    try {
      // Collecter tous les champs (sauf 'lang') présents dans les translations
      const allFields = new Set<string>();
      translations.forEach((trans) => {
        Object.keys(trans || {}).forEach((key) => {
          if (key !== 'lang') {
            allFields.add(key);
          }
        });
      });

      // Pour chaque champ, trouver une langue source qui a une valeur
      const sourceValues: Record<string, { value: string; lang: string }> = {};

      for (const field of allFields) {
        for (const trans of translations) {
          if (
            trans?.lang &&
            trans[field] &&
            typeof trans[field] === 'string' &&
            trans[field].trim() !== ''
          ) {
            sourceValues[field] = {
              value: trans[field],
              lang: trans.lang,
            };
            break;
          }
        }
      }

      // Vérifier qu'il y a au moins un champ source
      if (Object.keys(sourceValues).length === 0) {
        const error = new Error('No source values found');
        console.error(error.message);

        if (notification?.error) {
          this.rootStore.NotificationStore.emit({
            mode: 'ERROR',
            content: notification.error,
          });
        }

        this.setIsTranslating(false);
        onError?.(error);
        throw error;
      }

      // Traduire dans toutes les autres langues
      const updatedTranslations = await Promise.all(
        translations.map(async (trans) => {
          if (!trans?.lang) {
            return trans;
          }

          const targetLanguageCode =
            languageCodeMapper[trans.lang as Languages];
          const newTrans = { ...trans };

          // Pour chaque champ, traduire si nécessaire
          for (const [field, source] of Object.entries(sourceValues)) {
            // Ne pas traduire si la cible a déjà une valeur
            if (newTrans[field] && newTrans[field].trim() !== '') {
              continue;
            }

            // Ne pas traduire si c'est la même langue que la source
            if (trans.lang === source.lang) {
              continue;
            }

            // Ne pas spécifier la langue source pour permettre la détection automatique
            // Cela évite les problèmes quand on tape du texte dans une langue différente
            // de celle du champ (ex: taper du français dans le champ anglais)
            newTrans[field] = await this.translateText(
              source.value,
              targetLanguageCode,
              null, // Détection automatique de la langue source
              apiKey,
            );
          }

          return newTrans;
        }),
      );

      // Notification de succès
      if (notification?.success) {
        this.rootStore.NotificationStore.emit({
          mode: 'SUCCESS',
          content: notification.success,
        });
      }

      this.setIsTranslating(false);
      onSuccess?.();
      return updatedTranslations;
    } catch (error) {
      console.error('Auto translation failed:', error);

      if (notification?.error) {
        this.rootStore.NotificationStore.emit({
          mode: 'ERROR',
          content: notification.error,
        });
      }

      this.setIsTranslating(false);
      onError?.(error as Error);
      throw error;
    }
  }
}

export const ConfigAI = observer(
  ({
    children,
    apiKey,
  }: {
    children: React.ReactNode;
    apiKey: { translator?: string | null };
  }) => {
    const { AIStore } = useStoreCommon();

    useEffect(() => {
      if (!AIStore.load && apiKey) {
        logWithState({ state: 'INFO', value: 'ConfigAI init' });
        AIStore.init({ apiKey });
      }

      if (AIStore.load) {
        logWithState({ state: 'INFO', value: 'ConfigAI load' });
      }
    }, [AIStore.load, apiKey]);

    return AIStore.load && AIStore.googleTranslateApiKey ? children : null;
  },
);
